import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { usePresenceStore } from '../../stores/presenceStore';
import { SidePanel } from '../ui/SidePanel';
import KeyRotationPanel from './KeyRotationPanel';

interface Props {
  channelId: string;
  onClose: () => void;
  onStartDm: (userId: string) => void;
  /** Called after this channel has been permanently deleted. */
  onDeleted?: () => void;
}

interface MemberData {
  channelId: string;
  userId: string;
  role: string;
  user: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    status: string;
    email: string;
  };
}

export default function ChannelSettingsPanel({ channelId, onClose, onStartDm, onDeleted }: Props) {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const getStatus = usePresenceStore((s) => s.getStatus);
  const [editName, setEditName] = useState('');
  const [editTopic, setEditTopic] = useState('');
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { data: channel } = useQuery({
    queryKey: ['channel', channelId],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/channels/${channelId}`);
      return data.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = {};
      if (editName.trim()) body.name = editName;
      if (editTopic !== undefined) body.topic = editTopic;
      await api.patch(`/api/v1/channels/${channelId}`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
      setEditing(false);
    },
    onError: () => toast.error('Failed to update channel'),
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/api/v1/channels/${channelId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
    },
    onError: () => toast.error('Failed to remove member'),
  });

  const { data: orgs } = useQuery({
    queryKey: ['orgs'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/orgs');
      return data.data as Array<{ id: string; role: string }>;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/api/v1/channels/${channelId}`);
    },
    onSuccess: () => {
      toast.success('Channel deleted');
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.removeQueries({ queryKey: ['channel', channelId] });
      queryClient.removeQueries({ queryKey: ['messages', channelId] });
      onDeleted?.();
    },
    onError: () => {
      setConfirmingDelete(false);
      toast.error('Failed to delete channel');
    },
  });

  const members: MemberData[] = channel?.members || [];

  // Deletion is offered to the channel OWNER or an org OWNER/ADMIN — the
  // same rule the API enforces. DMs are never deletable.
  const myChannelRole = members.find((m) => m.userId === currentUserId)?.role;
  const myOrgRole = orgs?.find((o) => o.id === channel?.orgId)?.role;
  const canDelete =
    channel?.type !== 'DM' &&
    (myChannelRole === 'OWNER' || myOrgRole === 'OWNER' || myOrgRole === 'ADMIN');

  const statusColor = (userId: string) => {
    const s = getStatus(userId);
    if (s === 'ONLINE') return 'bg-accent-green';
    if (s === 'AWAY') return 'bg-accent-yellow';
    return 'bg-gray-400';
  };

  return (
    <SidePanel title="Channel Details" onClose={onClose} closeLabel="Close channel details">
      <div className="flex-1 overflow-y-auto p-4">
        {editing ? (
          <div className="mb-4 space-y-3">
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full rounded-input border border-warm-border px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:bg-warm-dark-elevated dark:border-warm-dark-border dark:text-white"
              placeholder="Channel name"
            />
            <input
              value={editTopic}
              onChange={(e) => setEditTopic(e.target.value)}
              className="w-full rounded-input border border-warm-border px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:bg-warm-dark-elevated dark:border-warm-dark-border dark:text-white"
              placeholder="Topic"
            />
            <div className="flex gap-2">
              <button
                onClick={() => updateMutation.mutate()}
                className="rounded-btn bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary-hover transition"
              >
                Save
              </button>
              <button
                onClick={() => setEditing(false)}
                className="rounded-btn px-3 py-1.5 text-sm text-warm-gray-600 hover:bg-warm-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-warm-gray-800 dark:text-white">
              #{channel?.name}
            </h4>
            <p className="mt-1 text-sm text-warm-gray-500">{channel?.topic || 'No topic set'}</p>
            <button
              onClick={() => {
                setEditName(channel?.name || '');
                setEditTopic(channel?.topic || '');
                setEditing(true);
              }}
              className="mt-2 text-sm text-primary hover:underline"
            >
              Edit
            </button>
          </div>
        )}

        <h4 className="mb-2 text-sm font-semibold text-warm-gray-700 dark:text-warm-gray-300">
          Members ({members.length})
        </h4>
        <div className="space-y-1">
          {members.map((m) => (
            <div
              key={m.userId}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-warm-gray-50 dark:hover:bg-white/5 transition"
            >
              <div className="relative flex h-7 w-7 items-center justify-center rounded bg-primary text-xs font-bold text-white">
                {m.user.avatarUrl ? (
                  <img src={m.user.avatarUrl} alt="" className="h-7 w-7 rounded" />
                ) : (
                  m.user.displayName[0]?.toUpperCase()
                )}
                <span
                  className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${statusColor(m.userId)}`}
                />
              </div>
              <div className="flex-1 overflow-hidden">
                <span className="truncate text-sm text-warm-gray-700 dark:text-warm-gray-300">
                  {m.user.displayName}
                </span>
                {m.role !== 'MEMBER' && (
                  <span className="ml-1 text-xs text-warm-gray-400">{m.role.toLowerCase()}</span>
                )}
              </div>
              {m.userId !== currentUserId && (
                <div className="flex gap-1">
                  <button
                    onClick={() => onStartDm(m.userId)}
                    title="Send DM"
                    className="rounded p-1 text-warm-gray-400 hover:bg-warm-gray-100 hover:text-primary"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => removeMutation.mutate(m.userId)}
                    title="Remove from channel"
                    className="rounded p-1 text-warm-gray-400 hover:bg-accent-coral-light hover:text-accent-coral"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 border-t border-warm-border pt-4 dark:border-warm-dark-border">
          {/* isAdmin mirrors the API's assertChannelAdmin gate (channel OWNER/ADMIN).
              "Trigger rotation" re-seals a fresh channel key to EVERY current
              member — the only repair path for members left without a key. */}
          <KeyRotationPanel
            channelId={channelId}
            isAdmin={myChannelRole === 'OWNER' || myChannelRole === 'ADMIN'}
          />
        </div>

        {canDelete && (
          <div className="mt-6 border-t border-warm-border pt-4 dark:border-warm-dark-border">
            <h4 className="mb-2 text-sm font-semibold text-accent-coral">Danger zone</h4>
            {confirmingDelete ? (
              <div className="rounded-lg border border-accent-coral/40 bg-accent-coral-light/30 p-3">
                <p className="text-sm text-warm-gray-700 dark:text-warm-gray-300">
                  Permanently delete <strong>#{channel?.name}</strong> and all of its messages and
                  files for every member? This cannot be undone.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="rounded-btn bg-accent-coral px-3 py-1.5 text-sm text-white transition hover:opacity-90 disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Yes, delete it'}
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    className="rounded-btn px-3 py-1.5 text-sm text-warm-gray-600 transition hover:bg-warm-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmingDelete(true)}
                className="rounded-btn border border-accent-coral/50 px-3 py-1.5 text-sm text-accent-coral transition hover:bg-accent-coral-light/40"
              >
                Delete this group
              </button>
            )}
          </div>
        )}
      </div>
    </SidePanel>
  );
}
